import type {OptimizedSchedule} from '@domain/schedule'
import {ApiClient} from '@hconnect/apiclient'
import {usePlantStartOfToday, useUpmPlantContext} from '@hconnect/upm'
import {useQuery} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {useCallback} from 'react'

import {
  ONE_MINUTE_REFRESH_INTERVAL,
  OPTIMIZER_RUNS_AND_SCHEDULES_QUERY_KEY,
  getOptimizedSchedulesPath,
  getOptimizerRunsPath
} from '../consts'
import {mapCalculatingPlansResponse, selectScheduleItemsFrom} from '../selectors'
import {OptimizerRun, OptimizerRunResponse} from '../types'

interface OptimizedSchedulesAndRunsParams {
  isDisabled?: boolean
  throwOnError?: boolean
}

interface OptimizerRunsAndSchedulesResponse {
  optimizerRuns: OptimizerRunResponse[]
  optimizedSchedules: OptimizedSchedule[]
}

interface OptimizerRunsAndSchedulesData {
  optimizerRuns: OptimizerRun[]
  optimizedSchedules: OptimizedSchedule[]
}

export const useOptimizedSchedulesAndRuns = ({
  isDisabled = false,
  throwOnError = true
}: OptimizedSchedulesAndRunsParams = {}) => {
  const {plantId: plantCode} = useUpmPlantContext()
  const startOfPlan = usePlantStartOfToday()

  const select = useCallback(
    (data: OptimizerRunsAndSchedulesResponse): OptimizerRunsAndSchedulesData => ({
      optimizerRuns: mapCalculatingPlansResponse(data.optimizerRuns),
      // Since in "/optimized" endpoint BE includes also schedules for ± 1 day around the plan range,
      // we need to filter out old schedule items that are no longer relevant.
      optimizedSchedules: selectScheduleItemsFrom(data.optimizedSchedules, startOfPlan)
    }),
    [startOfPlan]
  )

  return useQuery<OptimizerRunsAndSchedulesResponse, AxiosError, OptimizerRunsAndSchedulesData>({
    queryKey: [OPTIMIZER_RUNS_AND_SCHEDULES_QUERY_KEY, {plantCode}],
    queryFn: async ({signal}) => {
      const [runsResponse, schedulesResponse] = await Promise.all([
        ApiClient.axiosInstance.get<OptimizerRunResponse[]>(getOptimizerRunsPath(plantCode), {
          signal
        }),
        ApiClient.axiosInstance.get<OptimizedSchedule[]>(getOptimizedSchedulesPath(plantCode), {
          signal
        })
      ])
      return {
        optimizerRuns: runsResponse.data,
        optimizedSchedules: schedulesResponse.data
      }
    },
    enabled: !isDisabled,
    throwOnError,
    refetchInterval: ONE_MINUTE_REFRESH_INTERVAL,
    select
  })
}
